import React, { useState } from "react";
import axios from "axios";
import { X, Lock, Mail, CheckCircle2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginModal({ onClose, onAuthed }) {
  const [mode, setMode] = useState("password"); // "password" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const inputCls = "w-full bg-white border border-[#D2E0E6] text-[#0B2830] mono text-sm px-3 py-2.5 rounded-md focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none";

  const submitMagic = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/auth/magic/request`, { email, base: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      onAuthed && onAuthed(r.data.token, r.data.email, r.data.is_super, r.data.must_change_password);
      onClose && onClose();
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const submitForgot = async () => {
    if (!email.includes("@")) { setError("Enter your email first, then tap “Forgot password”."); return; }
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, base: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-[#0B2830]/40 flex items-center justify-center z-50 p-4" data-testid="login-modal">
      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-xl w-full max-w-sm fade-up">
        <div className="flex items-center justify-between border-b border-[#D2E0E6] px-5 py-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#0284C7]" />
            <h3 className="font-semibold text-[#0B2830]">Sign in</h3>
          </div>
          <button data-testid="login-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center space-y-3" data-testid="login-sent">
            <CheckCircle2 className="w-10 h-10 text-[#0D9488] mx-auto" />
            <h4 className="font-semibold text-[#0B2830]">Check your inbox</h4>
            <p className="text-sm text-[#47616B]">
              If <span className="mono text-[#0B2830]">{email}</span> has an account, a secure one-time
              link is on its way. It expires shortly, so use it soon.
            </p>
            <button data-testid="login-done" onClick={onClose}
              className="mt-2 w-full bg-transparent border border-[#D2E0E6] text-[#47616B] px-4 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830]">
              Close
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="inline-flex bg-[#E9F1F4] border border-[#D2E0E6] rounded-md p-0.5 w-full" data-testid="login-mode-toggle">
              <button data-testid="login-tab-password" onClick={() => { setMode("password"); setError(null); }}
                className={`flex-1 px-3 py-1.5 text-xs mono rounded transition-colors ${mode === "password" ? "bg-[#0284C7] text-white" : "text-[#47616B] hover:text-[#0B2830]"}`}>
                Password
              </button>
              <button data-testid="login-tab-magic" onClick={() => { setMode("magic"); setError(null); }}
                className={`flex-1 px-3 py-1.5 text-xs mono rounded transition-colors ${mode === "magic" ? "bg-[#0284C7] text-white" : "text-[#47616B] hover:text-[#0B2830]"}`}>
                Email link
              </button>
            </div>

            {mode === "password" ? (
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Admin email</label>
                  <input data-testid="login-email" type="email" value={email} autoFocus
                    onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@yourdomain.com" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Password</label>
                  <input data-testid="login-password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
                </div>
                {error && (
                  <div data-testid="login-error" className="text-sm text-[#E11D48] border border-[#E11D48]/30 bg-[#E11D48]/5 rounded-md px-3 py-2">{error}</div>
                )}
                <button data-testid="login-submit-password" type="submit" disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-[#0284C7] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-50">
                  <KeyRound className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
                </button>
                <button data-testid="login-forgot" type="button" onClick={submitForgot}
                  className="w-full text-center text-xs text-[#7C949C] hover:text-[#0284C7] transition-colors">
                  Forgot password?
                </button>
              </form>
            ) : (
              <form onSubmit={submitMagic} className="space-y-4">
                <p className="text-xs text-[#7C949C]">Enter the admin email and we'll send a secure one-time login link (requires email to be configured).</p>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Admin email</label>
                  <input data-testid="login-email" type="email" value={email} autoFocus
                    onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@yourdomain.com" />
                </div>
                {error && (
                  <div data-testid="login-error" className="text-sm text-[#E11D48] border border-[#E11D48]/30 bg-[#E11D48]/5 rounded-md px-3 py-2">{error}</div>
                )}
                <button data-testid="login-submit" type="submit" disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-[#0284C7] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-50">
                  <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Email me a login link"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
